import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowDown, ArrowRight } from "lucide-react";

import { UpdatedStamp } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";

import { getSummary, messageFor, UnitsSoldError, type TtuSummary } from "./api";
import { AttentionList } from "./components/AttentionList";
import { ContributionBar } from "./components/ContributionBar";
import { FlowCard } from "./components/FlowCard";
import { SummarySkeleton } from "./components/LoadingSkeletons";
import { NotComputedCard } from "./components/NotComputedCard";
import { ScopeNote } from "./components/ScopeNote";
import { ModuleControls, regionConflictNote, sourceScopeNote, useControls, type Filter } from "./controls";
import { emptyCopy, formatShortDate, formatTtuPct } from "./format";

export function openTabTarget(tab: string, search: Record<string, unknown>) {
  return {
    to: "/app/$area/$module/$tab" as const,
    params: { area: "customer_acquisition", module: "meteoric_units_sold", tab },
    search,
  };
}

export function SummaryPage({ areaName }: { areaName: string }) {
  const controls = useControls();
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const summary = useQuery({ queryKey: ["meteoric_units_sold", "summary", controls.toQuery()], queryFn: () => getSummary(controls.toQuery()) });
  const open = (tab: string) => { void navigate(openTabTarget(tab, search)); };
  const data = summary.data?.data;
  const unavailableAnswer = data ? notMeasuredAnswer(data) : null;
  const noSalesAnswer = data ? emptySummaryAnswer(data, controls.filter, controls.channels) : null;
  const answer = unavailableAnswer ?? noSalesAnswer ?? (data ? answerLine(data) : null);
  const showContribution = !unavailableAnswer && !noSalesAnswer;
  const conflictNote = data ? regionConflictNote(controls.filter, data.scope) : null;
  const sourceNote = data ? sourceScopeNote(controls.filter, data.scope.excluded_channel) : null;
  const lagNote = data ? summaryLagNote(data) : null;
  return <div className="flex flex-col gap-6">
    <div className="flex flex-wrap items-center justify-between gap-3"><ModuleControls controls={controls} />{data && <UpdatedStamp generatedAt={data.generated_at} />}</div>
    {summary.isPending ? <SummarySkeleton /> : summary.isError ? (
      summary.error instanceof UnitsSoldError && summary.error.status === 404 ? <NotComputedCard /> :
      <Alert><AlertTitle>Summary unavailable</AlertTitle><AlertDescription>{messageFor(summary.error, areaName)}</AlertDescription></Alert>
    ) : data && (conflictNote ? (
      <ScopeNote>{conflictNote}</ScopeNote>
    ) : <>
      {sourceNote && <ScopeNote>{sourceNote}</ScopeNote>}
      {data.scope.note && <ScopeNote>{data.scope.note}</ScopeNote>}
      {lagNote && <ScopeNote>{lagNote}</ScopeNote>}
      <Card><CardContent className="grid gap-5">
        {answer && <p className="text-xl font-semibold text-ks-ink" tabIndex={0}>{answer}</p>}
        {showContribution && <ContributionBar contribution={data.contribution} steps={data.steps} />}
      </CardContent></Card>
      <div className="grid grid-cols-1 items-stretch gap-3 md:grid-cols-[1fr_auto_1fr_auto_.75fr_auto_1fr]">
        <FlowCard label="Traffic" step={data.steps.traffic} mover={data.contribution.mover === "traffic"} onOpen={() => open("traffic")} />
        <FlowArrow />
        <FlowCard label="Conversion" step={data.steps.conversion} mover={data.contribution.mover === "conversion"} onOpen={() => open("conversion")} />
        <FlowArrow />
        <FlowCard label="Units per order" step={data.steps.units_per_order} mover={data.contribution.mover === "units_per_order"} />
        <FlowArrow />
        <FlowCard label="Units" step={data.steps.units} onOpen={() => open("units")} />
      </div>
      <AttentionList items={data.attention} limit={3} seeAll search={search} />
    </>)}
  </div>;
}

export function answerLine(data: TtuSummary) {
  if (data.contribution.mover === null || data.contribution.units_yoy_pct === null) return null;
  const change = data.contribution.units_yoy_pct;
  const direction = change < 0 ? "down" : "up";
  const mover = data.contribution.mover === "units_per_order" ? "Units per order" : data.contribution.mover === "conversion" ? "Conversion" : "Traffic";
  return `Units are ${direction} ${formatTtuPct(Math.abs(change)).replace("+", "")} on last year. ${mover} explains most of it.`;
}

export function notMeasuredAnswer(data: TtuSummary): string | null {
  return !data.steps.traffic.measured && !data.steps.conversion.measured
    ? "Traffic and conversion are not measured for this channel. Units are shown below."
    : null;
}

export function emptySummaryAnswer(data: TtuSummary, filter: Filter, channels: string[]): string | null {
  if (data.steps.units.value !== null && data.steps.units.value !== 0) return null;
  return emptyCopy("sales", filter.product, channels);
}

export function summaryLagNote(data: TtuSummary): string | null {
  const conversion = data.steps.conversion;
  if (!conversion.lagging_source || !conversion.data_through) return null;
  const source = conversion.lagging_source === "amazon"
    ? "Amazon"
    : conversion.lagging_source === "shopify" ? "Shopify" : conversion.lagging_source;
  const unitsThrough = data.steps.units.data_through;
  const unitsSentence = unitsThrough && unitsThrough !== conversion.data_through
    ? ` Units run to ${formatShortDate(unitsThrough)}.`
    : "";
  return `Conversion uses data to ${formatShortDate(conversion.data_through)}, the latest date ${source} traffic is complete.${unitsSentence}`;
}

function FlowArrow() {
  return <span aria-hidden className="flex items-center justify-center text-muted-foreground"><ArrowRight className="hidden size-5 md:block" /><ArrowDown className="size-5 md:hidden" /></span>;
}
